/**
 * Board-selection outcome reporting for the pipeline switcher (SE-6270).
 *
 * The chip row and the "See all pipelines" drawer both pick a board by id and
 * hand it to `useBoardManager.switchBoard`. SE-7120 gave `switchBoard` a miss
 * signal — it returns `false` when the id is not in the resolved board list —
 * and wired that signal into `useBoardDeepLink`, which retries a provisional
 * miss and surfaces a conclusive one. The SWITCHER path never consumed it:
 * `MainContainer.handleBoardSelect` called `switchBoard(boardId)` and discarded
 * the boolean, so a pick the manager could not resolve did nothing at all — no
 * board change, no error, no toast. The drawer closed and the user was still on
 * the old board, unable to tell whether they mis-clicked, the app was slow, or
 * the pipeline was broken.
 *
 * ## Why a listed pipeline can still be unresolvable
 *
 * The ticket blamed a page-size mismatch (`first: 40` in the board manager
 * vs. 100/page in the switcher). That is no longer the cause: SE-6575 replaced
 * the manager's bare `first: 40` with `useAllMyKanbanBoards`, which walks the
 * whole connection at `BOARD_LIST_PAGE_SIZE` per page up to the same
 * ceiling the switcher uses (100 x 20 = 2000 boards on both sides). The two
 * readers are now symmetric in SIZE.
 *
 * They are not symmetric in TIME or in FAILURE, because they are two distinct
 * queries (`ListMyKanbanBoards` and `PipelineSwitcherBoards`) occupying
 * separate normalized cache entries and walking independently:
 *
 *  - **Mid-walk.** The manager's walk restarts from page 1 whenever the
 *    connection is replaced under it — a `cache-and-network` background fetch,
 *    any `refetchQueries: ["ListMyKanbanBoards"]` (board sharing, board
 *    mutations), or a delete pruning an edge. While it re-walks, `boards` holds
 *    one page and the drawer — whose own query was not refetched — still lists
 *    every board.
 *  - **Truncated.** The manager's walk can stop short (page cap, a failed page,
 *    a page claiming more with no cursor) while the switcher's completed.
 *  - **First page unreadable.** SE-7338: an aborted transport leaves the
 *    manager with an EMPTY connection and `loading` already false, while the
 *    drawer is fully populated.
 *
 * In each case the drawer offers a board the manager does not have. That is the
 * ticket's symptom, reached by a different road than the one it names.
 *
 * ## Four distinct answers, never collapsed into one
 *
 * A silent no-op is the worst shape, but inventing an error for a board that is
 * merely still loading is barely better. {@link BoardSelectionOutcome} keeps the
 * four states apart and every consumer switches over them exhaustively, so a
 * fifth state cannot be added without the compiler naming every site that must
 * handle it:
 *
 *  - `switched` — resolved and activated. Nothing to say.
 *  - `pending` — the board set can still grow, so the miss is PROVISIONAL. The
 *    pick is held and retried as the set arrives (the discipline
 *    `useBoardDeepLink` uses for the same reason); the user is not told
 *    anything, because nothing has failed yet.
 *  - `incomplete` — the board set settled but is known NOT to be whole
 *    (`hasUnreachableBoards`). The board may well exist; we cannot see it. That
 *    is a different sentence from "it is gone".
 *  - `absent` — the set settled, is whole, and does not contain the board. It
 *    was deleted, or this viewer's role cannot see it.
 * @module features/player-kanban/hooks/useBoardSelection
 */

import { useCallback, useEffect, useRef } from "react";

/**
 * How a board pick resolved. Discriminated on `kind` so consumers switch
 * exhaustively rather than testing a boolean that cannot express "not yet".
 */
export type BoardSelectionOutcome =
  | {
      /** The board was found and is now active. */
      readonly kind: "switched";
      /** The board that was activated. */
      readonly boardId: string;
    }
  | {
      /** The board set can still grow; the pick is held and retried. */
      readonly kind: "pending";
      /** The board being waited on. */
      readonly boardId: string;
    }
  | {
      /** The board set settled but is known to be missing boards. */
      readonly kind: "incomplete";
      /** The board that could not be resolved. */
      readonly boardId: string;
    }
  | {
      /** The board set settled, is whole, and does not contain the board. */
      readonly kind: "absent";
      /** The board that could not be resolved. */
      readonly boardId: string;
    };

/** Inputs to {@link resolveBoardSelectionOutcome}. */
export interface BoardSelectionInputs {
  /** The board id the user picked. */
  readonly boardId: string;
  /** Whether `switchBoard` matched the id and activated the board. */
  readonly didSwitch: boolean;
  /**
   * True while the resolved board set can still CHANGE — the connection walk is
   * still fetching pages, or the inputs that decide the source type have not
   * settled. A miss under this is provisional.
   */
  readonly boardSetPending: boolean;
  /**
   * True when the board set is known NOT to be the whole set — the walk stopped
   * while the connection still reported pages, or the first page never landed.
   */
  readonly hasUnreachableBoards: boolean;
}

/**
 * Classify one board pick. Pure, so the four-way distinction is testable
 * without a React tree.
 *
 * Order matters and encodes the honesty rule: a still-growing set is reported
 * as `pending` BEFORE any failure verdict, and an incomplete set is reported as
 * `incomplete` rather than `absent`, because "we cannot see it" is not
 * evidence that it is gone.
 * @param inputs - The {@link BoardSelectionInputs}.
 * @returns The {@link BoardSelectionOutcome}.
 */
export const resolveBoardSelectionOutcome = (
  inputs: BoardSelectionInputs
): BoardSelectionOutcome => {
  const { boardId, didSwitch, boardSetPending, hasUnreachableBoards } = inputs;
  if (didSwitch) return { kind: "switched", boardId };
  if (boardSetPending) return { kind: "pending", boardId };
  if (hasUnreachableBoards) return { kind: "incomplete", boardId };
  return { kind: "absent", boardId };
};

/** Arguments for {@link useBoardSelection}. */
export interface UseBoardSelectionArgs {
  /**
   * Activates the board with the given id and reports whether it matched.
   * `false` means nothing changed.
   */
  readonly switchBoard: (boardId: string) => boolean;
  /** See {@link BoardSelectionInputs.boardSetPending}. */
  readonly boardSetPending: boolean;
  /** See {@link BoardSelectionInputs.hasUnreachableBoards}. */
  readonly hasUnreachableBoards: boolean;
  /**
   * Called with the SETTLED outcome of a pick. Never called with `pending` —
   * a provisional miss is retried, not reported. It IS called with `switched`,
   * including for a pick that only landed on a later retry, so a consumer can
   * clear a "still opening" indication it raised.
   */
  readonly onOutcome: (outcome: BoardSelectionOutcome) => void;
}

/**
 * Wrap `switchBoard` so a pick from the chip row or the See-all drawer reports
 * what happened instead of silently doing nothing.
 *
 * A pick that misses while the board set is still arriving is HELD in a ref and
 * re-attempted whenever the set changes — `switchBoard`'s identity is keyed on
 * the resolved board list, so it changes exactly when there is something new to
 * try. The held pick therefore lands by itself the moment its board arrives,
 * which is the first acceptance criterion ("selecting any listed pipeline
 * switches to it") rather than merely an error message about failing to.
 *
 * A ref rather than state on purpose: `react-hooks/set-state-in-effect` bars
 * clearing the pending pick from the retry effect, and no render depends on the
 * pending id — the only visible consequence is the `onOutcome` call, which the
 * effect makes directly.
 * @param args - The {@link UseBoardSelectionArgs}.
 * @param args.switchBoard - Activates a board by id; `false` means no match.
 * @param args.boardSetPending - True while the resolved board set can still grow.
 * @param args.hasUnreachableBoards - True when the board set is known incomplete.
 * @param args.onOutcome - Receives the settled outcome of a pick.
 * @returns `selectBoard`, a stable handler to hand to the switcher surfaces.
 */
export const useBoardSelection = ({
  switchBoard,
  boardSetPending,
  hasUnreachableBoards,
  onOutcome,
}: UseBoardSelectionArgs): ((boardId: string) => void) => {
  /** The pick being waited on, or null. */
  const pendingBoardIdRef = useRef<string | null>(null);

  const attempt = useCallback(
    (boardId: string): BoardSelectionOutcome =>
      resolveBoardSelectionOutcome({
        boardId,
        didSwitch: switchBoard(boardId),
        boardSetPending,
        hasUnreachableBoards,
      }),
    [switchBoard, boardSetPending, hasUnreachableBoards]
  );

  const selectBoard = useCallback(
    (boardId: string): void => {
      const outcome = attempt(boardId);
      if (outcome.kind === "pending") {
        // Hold it: the board set is still arriving and the retry effect below
        // will land this pick as soon as the board shows up.
        pendingBoardIdRef.current = boardId;
        return;
      }
      // A new settled pick supersedes any held one — otherwise a stale pending
      // board could yank the user off the board they just successfully opened.
      pendingBoardIdRef.current = null;
      onOutcome(outcome);
    },
    [attempt, onOutcome]
  );

  useEffect(() => {
    const boardId = pendingBoardIdRef.current;
    if (boardId === null) return;

    const outcome = attempt(boardId);
    if (outcome.kind === "pending") return;

    pendingBoardIdRef.current = null;
    onOutcome(outcome);
  }, [attempt, onOutcome]);

  return selectBoard;
};
